// src/components/TenantProfilePage.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../firebase";
import useRole from "../hooks/useRole";
import { logTenantHistory } from "../helpers/historyLogger";

export default function TenantProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit, canDelete } = useRole();

  const [tenant, setTenant] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  const [form, setForm] = useState({
    fullName: "",
    gender: "Male",
    phone: "",
    email: "",
    occupation: "",
    idNumber: "",
    emergencyContact: "",
    emergencyRelation: "",
    assignedRoom: "",
    checkInDate: "",
    status: "Active",
  });

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  useEffect(() => {
    const loadAll = async () => {
      try {
        const snap = await getDoc(doc(db, "tenants", id));
        if (snap.exists()) {
          const data = { id: snap.id, ...snap.data() };
          setTenant(data);
          setForm({ ...data });
        }

        const q = query(
          collection(db, "tenants", id, "history"),
          orderBy("timestamp", "desc")
        );
        const hSnap = await getDocs(q);
        setHistory(hSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, [id]);

  const handleToggleStatus = async () => {
    if (!tenant) return;
    const newStatus = tenant.status === "Active" ? "Left" : "Active";

    await updateDoc(doc(db, "tenants", tenant.id), { status: newStatus });
    await logTenantHistory(tenant.id, {
      type: "status-change",
      message: `Status changed from ${tenant.status} → ${newStatus}`,
    });

    setTenant((prev) => ({ ...prev, status: newStatus }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();

    await updateDoc(doc(db, "tenants", tenant.id), form);

    await logTenantHistory(tenant.id, {
      type: "update",
      message: "Tenant information updated",
    });

    setTenant((prev) => ({ ...prev, ...form }));
    setIsEditing(false);
  };

  if (loading) return <div className="p-6">Loading tenant profile...</div>;

  if (!tenant)
    return (
      <div className="p-6">
        <button
          onClick={() => navigate("/dashboard/tenants")}
          className="mb-4 px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
        >
          Back
        </button>
        <p>Tenant not found.</p>
      </div>
    );

  const fields = [
    ["fullName", "Full Name"],
    ["phone", "Phone"],
    ["email", "Email"],
    ["occupation", "Occupation"],
    ["idNumber", "ID / Passport"],
    ["emergencyContact", "Emergency Contact"],
    ["emergencyRelation", "Relation"],
    ["assignedRoom", "Room"],
  ];

  return (
    <div className="space-y-6">

      {/* BACK BUTTON */}
      <button
        onClick={() => navigate("/dashboard/tenants")}
        className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm"
      >
        Back
      </button>

      {/* HEADER CARD */}
      <div className="bg-white rounded-2xl shadow-lg p-6 flex items-center gap-6 border border-amber-50">
        <div className="w-20 h-20 rounded-full bg-[#b4642d] text-white flex items-center justify-center text-3xl font-bold">
          {getInitials(tenant.fullName)}
        </div>

        <div className="flex-1">
          <h1 className="text-2xl font-bold">{tenant.fullName}</h1>
          <p className="text-gray-600">{tenant.email || "No email"}</p>

          <span
            className={`inline-block mt-2 px-3 py-1 text-sm rounded-full text-white ${
              tenant.status === "Active" ? "bg-green-600" : "bg-yellow-600"
            }`}
          >
            {tenant.status}
          </span>
        </div>

        {/* ACTION BUTTONS */}
        <div className="flex flex-col gap-2">
          {canEdit && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-[#b4642d] text-white rounded-lg text-sm hover:bg-[#944d22]"
            >
              Edit Profile
            </button>
          )}

          {canEdit && (
            <button
              onClick={handleToggleStatus}
              className="px-4 py-2 bg-[#d18b3c] text-white rounded-lg text-sm"
            >
              {tenant.status === "Active" ? "Mark as Left" : "Mark as Active"}
            </button>
          )}

          {canDelete && (
            <button
              onClick={() => navigate("/dashboard/tenants")}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {/* EDIT FORM */}
      {isEditing && (
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-amber-50">
          <h2 className="text-xl font-bold mb-4">Edit Tenant Details</h2>

          <form onSubmit={handleSaveProfile} className="grid gap-4 md:grid-cols-2">
            {fields.map(([key, label]) => (
              <div key={key}>
                <label className="block text-sm font-medium mb-1">{label}</label>
                <input
                  name={key}
                  value={form[key] || ""}
                  onChange={handleFormChange}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            ))}

            <div>
              <label className="block text-sm font-medium mb-1">Gender</label>
              <select
                name="gender"
                value={form.gender}
                onChange={handleFormChange}
                className="w-full border rounded px-3 py-2"
              >
                <option>Male</option>
                <option>Female</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Check-In Date</label>
              <input
                type="date"
                name="checkInDate"
                value={form.checkInDate || ""}
                onChange={handleFormChange}
                className="w-full border rounded px-3 py-2"
              />
            </div>

            <div className="md:col-span-2 flex justify-end gap-3 mt-2">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
              >
                Cancel
              </button>

              <button
                type="submit"
                className="px-4 py-2 bg-[#b4642d] text-white rounded hover:bg-[#944d22]"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      )}

      {/* INFO */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-amber-50">
        <h2 className="text-xl font-bold mb-4">Tenant Information</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <p><strong>Phone:</strong> {tenant.phone}</p>
          <p><strong>Email:</strong> {tenant.email}</p>
          <p><strong>Occupation:</strong> {tenant.occupation}</p>
          <p><strong>ID/Passport:</strong> {tenant.idNumber}</p>
          <p><strong>Room:</strong> {tenant.assignedRoom}</p>
          <p><strong>Check-In:</strong> {tenant.checkInDate}</p>
          <p><strong>Emergency Contact:</strong> {tenant.emergencyContact}</p>
          <p><strong>Relation:</strong> {tenant.emergencyRelation}</p>
        </div>
      </div>

      {/* HISTORY */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-amber-50">
        <h2 className="text-xl font-bold mb-3">History</h2>

        {history.length === 0 ? (
          <p className="text-gray-500 text-sm">No history recorded.</p>
        ) : (
          <ul className="space-y-3 text-sm">
            {history.map((h) => (
              <li key={h.id} className="border-l-4 border-[#b4642d] pl-3">
                <p>{h.message}</p>
                <p className="text-xs text-gray-500">
                  {h.timestamp?.toDate().toLocaleString()}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
